'use client'

import React from 'react'
import Link from 'next/link'
import { Box, Flex, Text, Title } from '@mantine/core'
import { motion } from 'framer-motion'
import { CardSlider } from '@/components/Refresh/CardSlider/CardSlider'
import { CardSliderCard } from '@/components/Refresh/CardSlider/CardSliderCard'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { imageScaleVariants, staggerConfig, textFadeInVariantsNoTransition } from '@/lib/animations'
import { parseHtml } from '@/lib/parseHtml'
import type { SkuComponentConfigMap } from '@/types/enums/sku-component-types'

export function SkuSupportWidget({ config }: { config: SkuComponentConfigMap['skuSupportWidget'] }) {
    const { isSm } = useBreakpoint()
    const { title, bodyHtml, cards } = config

    return (
        <Box pos="relative" maw={1440} w="100%" mx="auto" id="skuSupportWidget">
            <Flex
                pos="relative"
                direction="column"
                align="center"
                gap={32}
                py={isSm ? 32 : 48}
                px={0}
                bg="#e8e6ed"
                style={{ borderRadius: isSm ? 16 : 24 }}
            >
                <Flex
                    pos="relative"
                    direction="column"
                    align={isSm ? 'flex-start' : 'center'}
                    ta={isSm ? 'left' : 'center'}
                    w="100%"
                    maw={720}
                    px={32}
                    py={0}
                >
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: '-200px 0px' }}
                        variants={{
                            hidden: { opacity: 0 },
                            visible: {
                                opacity: 1,
                                transition: staggerConfig,
                            },
                        }}
                        style={{ width: '100%' }}
                    >
                        <Flex
                            direction="column"
                            align={isSm ? 'flex-start' : 'center'}
                            justify="center"
                            gap={24}
                            w="100%"
                        >
                            <motion.div variants={textFadeInVariantsNoTransition} style={{ width: '100%' }}>
                                <Title
                                    order={2}
                                    m={0}
                                    p={0}
                                    w="100%"
                                    c="var(--gm-fg-primary-black)"
                                    fz={isSm ? 40 : 56}
                                    fw={500}
                                    lh={1}
                                    style={{ letterSpacing: isSm ? '-1.6px' : '-2.24px' }}
                                >
                                    {title}
                                </Title>
                            </motion.div>

                            <motion.div variants={textFadeInVariantsNoTransition}>
                                <Flex
                                    direction="column"
                                    gap={16}
                                    w="100%"
                                    maw={720}
                                    fz={isSm ? 18 : 20}
                                    fw={500}
                                    lh={1.3}
                                    c="#4f4e4d"
                                >
                                    {parseHtml(bodyHtml)}
                                </Flex>
                            </motion.div>
                        </Flex>
                    </motion.div>
                </Flex>

                <Box pos="relative" w="100%">
                    <CardSlider gap={24} startX={48} endGap={48}>
                        {cards.map((card, index) => {
                            const uniqueKey = `${card.title}-${index}`
                            const alt = card.imageAlt ?? card.title
                            const imageSrc = typeof card.imageSrc === 'string' ? card.imageSrc : card.imageSrc.src

                            const cardContent = (
                                <Flex
                                    direction="column"
                                    justify="flex-start"
                                    gap={64}
                                    w={285}
                                    h="100%"
                                    p={24}
                                    bg="rgba(255, 255, 255, 0.75)"
                                    style={{ borderRadius: 16 }}
                                >
                                    <Box
                                        w="100%"
                                        h={121}
                                        style={{ borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}
                                    >
                                        <motion.img
                                            src={imageSrc}
                                            alt={alt}
                                            draggable={false}
                                            onDragStart={(e) => e.preventDefault()}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true, margin: '-200px 0px' }}
                                            variants={imageScaleVariants}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                userSelect: 'none',
                                            }}
                                        />
                                    </Box>
                                    <Flex direction="column" gap={16}>
                                        <Text
                                            m={0}
                                            c="#000"
                                            fz={28}
                                            fw={400}
                                            lh="110%"
                                            style={{ letterSpacing: '0.279px' }}
                                        >
                                            {card.title}
                                        </Text>
                                        <Text
                                            m={0}
                                            p={0}
                                            c="var(--gm-fg-tertiery)"
                                            fz={isSm ? 16 : 18}
                                            fw={400}
                                            lh={1.3}
                                            style={{ letterSpacing: isSm ? '-0.2px' : '-0.36px' }}
                                        >
                                            {card.description}
                                        </Text>
                                    </Flex>
                                </Flex>
                            )

                            return (
                                <CardSliderCard key={uniqueKey}>
                                    <Link
                                        href={card.href}
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                        draggable={false}
                                    >
                                        {cardContent}
                                    </Link>
                                </CardSliderCard>
                            )
                        })}
                    </CardSlider>
                </Box>
            </Flex>
        </Box>
    )
}
